import React from 'react';
import { CardSelector, type CardItem } from '@/components/ui/CardSelector';
import { Badge } from '@/components/ui';
import { OPERATING_MODES } from './types';
import { Rocket, TrendingUp, Building2 } from 'lucide-react';
import { t } from '@/lib/i18n';

const MODE_ICONS: Record<string, React.ReactNode> = {
  founder: <Rocket className="w-8 h-8" />,
  growth: <TrendingUp className="w-8 h-8" />,
  scaling: <Building2 className="w-8 h-8" />,
};

interface StepOperatingModeProps {
  value: string;
  onChange: (mode: string) => void;
  autoFocus?: boolean;
}

export function StepOperatingMode({ value, onChange, autoFocus }: StepOperatingModeProps) {
  const items: CardItem[] = OPERATING_MODES.map((mode) => ({
    id: mode.id,
    title: mode.title(),
    description: mode.description(),
    icon: MODE_ICONS[mode.id],
  }));

  const handleChange = (selected: string | string[]) => {
    // Single mode always returns a string
    onChange(typeof selected === 'string' ? selected : selected[0]);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Choose your Operating Mode</h2>
        <p className="text-sm text-muted-foreground mt-1">This determines how strictly quality, governance, and cadence are enforced. You can change it later.</p>
      </div>

      <CardSelector
        items={items}
        selected={value}
        onChange={handleChange}
        mode="single"
        columns={3}
        autoFocus={autoFocus}
      />

      {/* Mode detail summary below cards */}
      {value && (() => {
        const mode = OPERATING_MODES.find((m) => m.id === value);
        if (!mode) return null;
        return (
          <div className="rounded-lg bg-muted border border-border p-4 space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <span className="font-medium text-muted-foreground min-w-[80px]">{t("QC Density")}:</span>
              <span className="text-muted-foreground">{t(mode.qcDensity)}</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="font-medium text-muted-foreground min-w-[80px]">{t("AI Default")}:</span>
              <span className="text-muted-foreground">{t(mode.aiDefaults)}</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="font-medium text-muted-foreground min-w-[80px]">{t("Cadence")}:</span>
              <span className="text-muted-foreground">{t(mode.cadence)}</span>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
